/**
 * Base des radars fixes.
 *
 * Source unique : les nœuds `highway=speed_camera` d'OpenStreetMap, relayés
 * par `api/radars.php`. La vitesse manque sur environ un tiers des nœuds ;
 * elle vaut alors zéro et n'est pas affichée.
 *
 * **Un cache par pays.** Ajouter un pays ne télécharge que lui, en retirer un
 * n'invalide pas les autres, et un échec au milieu d'une série conserve ce qui
 * a déjà réussi. Les téléchargements sont donc séquentiels : les réponses
 * restent petites, ce qui limite les délais dépassés d'Overpass, et la
 * progression peut nommer le pays en cours.
 *
 * Référence : `RadarManager.kt`, `docs/radars.md`.
 */

import type { Storage } from './storage'
import { SUPPORTED_RADAR_COUNTRIES, type RadarCountry } from './preferences'

/** Chemin relatif : l'application peut vivre dans un sous-répertoire. */
const RADARS_ENDPOINT = './api/radars.php'

/** La base bouge lentement ; un rafraîchissement hebdomadaire suffit. */
const REFRESH_INTERVAL_MS = 7 * 24 * 3600 * 1000

export interface RadarPoint {
  /** Identifiant du nœud OpenStreetMap. */
  readonly id: string
  readonly lat: number
  readonly lon: number
  /** Vitesse en km/h, `0` quand elle n'est pas renseignée. */
  readonly speedLimit: number
}

/** Étape rapportée pendant un rafraîchissement. */
export type RefreshStage = 'downloading' | 'processing'

export interface RefreshProgress {
  readonly stage: RefreshStage
  readonly country: RadarCountry
  /** Octets reçus pour le pays en cours. */
  readonly bytes: number
}

export interface RefreshResult {
  readonly radars: readonly RadarPoint[]
  /** Pays dont le téléchargement a échoué. */
  readonly failed: readonly RadarCountry[]
}

function cacheKey(country: RadarCountry): string {
  return `radars_${country}`
}

export class RadarManager {
  #byCountry = new Map<RadarCountry, readonly RadarPoint[]>()
  #merged: readonly RadarPoint[] = []

  /**
   * Rafraîchissement en cours.
   *
   * Plusieurs déclencheurs se présentent quasi simultanément au démarrage ;
   * sans cette sérialisation, ils lanceraient des téléchargements Overpass
   * parallèles sur un service public à quota.
   */
  #pending: Promise<RefreshResult> | null = null

  constructor(private readonly storage: Storage) {}

  /** Radars de tous les pays retenus, fusionnés. */
  get radars(): readonly RadarPoint[] {
    return this.#merged
  }

  /** Pays retenus dont les données manquent — jamais téléchargées, ou en échec. */
  missingCountries(countries: readonly RadarCountry[]): RadarCountry[] {
    return countries.filter((country) => !this.#byCountry.has(country))
  }

  /** Au moins un pays dispose de données exploitables. */
  hasLocalData(): boolean {
    return this.#merged.length > 0
  }

  /**
   * Charge depuis le cache local, sans aucun appel réseau.
   *
   * Les pays absents du cache sont simplement ignorés : c'est à
   * `needsRefresh` de signaler qu'un téléchargement s'impose.
   */
  async loadLocal(countries: readonly RadarCountry[]): Promise<void> {
    const next = new Map<RadarCountry, readonly RadarPoint[]>()

    for (const country of countries) {
      const cached = await this.storage.getCache<RadarPoint[]>(cacheKey(country))
      if (cached && cached.length > 0) next.set(country, cached)
    }

    this.#byCountry = next
    this.#remerge()
  }

  /**
   * Un téléchargement est-il nécessaire ?
   *
   * Vrai dès qu'un pays retenu n'a pas de données — cas de l'ajout d'un pays —
   * ou que le dernier rafraîchissement remonte à plus d'une semaine.
   */
  needsRefresh(
    countries: readonly RadarCountry[],
    lastRefresh: number,
    now = Date.now(),
  ): boolean {
    if (countries.some((country) => !this.#byCountry.has(country))) return true
    return lastRefresh <= 0 || now - lastRefresh > REFRESH_INTERVAL_MS
  }

  /**
   * Télécharge les pays retenus, un par un.
   *
   * @param force Retélécharger même les pays déjà en cache, pour le bouton
   *   manuel des réglages.
   * @returns Les radars fusionnés et la liste des pays en échec. L'appelant ne
   *   doit enregistrer la date de rafraîchissement que si cette liste est
   *   vide, sans quoi un échec partiel serait pris pour un succès et le
   *   pays manquant ne serait retenté qu'une semaine plus tard.
   */
  async refresh(
    countries: readonly RadarCountry[],
    options: { force?: boolean; onProgress?: (progress: RefreshProgress) => void } = {},
  ): Promise<RefreshResult> {
    // Un appel qui arrive derrière un rafraîchissement en cours attend son
    // résultat plutôt que d'en lancer un second.
    if (this.#pending !== null && options.force !== true) return this.#pending

    const run = this.#refresh(countries, options)
    this.#pending = run

    try {
      return await run
    } finally {
      if (this.#pending === run) this.#pending = null
    }
  }

  async #refresh(
    countries: readonly RadarCountry[],
    options: { force?: boolean; onProgress?: (progress: RefreshProgress) => void },
  ): Promise<RefreshResult> {
    const failed: RadarCountry[] = []

    for (const country of countries) {
      if (options.force !== true && this.#byCountry.has(country)) continue

      options.onProgress?.({ stage: 'downloading', country, bytes: 0 })

      const radars = await this.#fetchCountry(country, (bytes) =>
        options.onProgress?.({ stage: 'downloading', country, bytes }),
      )

      if (radars === null) {
        failed.push(country)
        continue
      }

      options.onProgress?.({ stage: 'processing', country, bytes: 0 })

      this.#byCountry.set(country, radars)
      await this.storage.putCache(cacheKey(country), radars)
    }

    this.#remerge()
    return { radars: this.#merged, failed }
  }

  /**
   * Aligne le cache sur la sélection de pays.
   *
   * Les pays retirés sont oubliés — mémoire et cache — et les manquants
   * téléchargés.
   */
  async applyCountrySelection(
    countries: readonly RadarCountry[],
    onProgress?: (progress: RefreshProgress) => void,
  ): Promise<RefreshResult> {
    await this.purgeUnselected(countries)
    return this.refresh(countries, { onProgress })
  }

  /**
   * Supprime le cache des pays qui ne sont plus retenus.
   *
   * Le balayage porte sur **tous** les pays pris en charge, et non sur ceux
   * chargés en mémoire : une sélection modifiée alors que le cache n'avait pas
   * été lu laisserait sinon une entrée orpheline, jamais relue mais occupant
   * durablement le stockage du navigateur.
   */
  async purgeUnselected(countries: readonly RadarCountry[]): Promise<void> {
    const kept = new Set<string>(countries)

    for (const country of SUPPORTED_RADAR_COUNTRIES) {
      if (kept.has(country)) continue

      this.#byCountry.delete(country)
      await this.storage.deleteCache(cacheKey(country))
    }

    this.#remerge()
  }

  /**
   * Oublie les données en mémoire sans toucher au cache.
   *
   * Sert au bouton de rafraîchissement manuel : sans cela, un pays déjà en
   * cache serait sauté et le bouton n'aurait aucun effet visible.
   */
  forgetAll(): void {
    this.#byCountry.clear()
    this.#remerge()
  }

  #remerge(): void {
    const merged: RadarPoint[] = []
    for (const radars of this.#byCountry.values()) merged.push(...radars)
    this.#merged = merged
  }

  /**
   * Télécharge un pays.
   *
   * @returns `null` sur échec — miroir indisponible, délai dépassé, réponse
   *   inexploitable. Une réponse vide est aussi un échec : chaque pays pris en
   *   charge compte des centaines de radars, et zéro trahit un problème plutôt
   *   qu'une absence réelle.
   */
  async #fetchCountry(
    country: RadarCountry,
    onBytes: (bytes: number) => void,
  ): Promise<RadarPoint[] | null> {
    try {
      const response = await fetch(`${RADARS_ENDPOINT}?country=${country}`)
      if (!response.ok) return null

      const text = await readWithProgress(response, onBytes)
      const payload: unknown = JSON.parse(text)

      if (typeof payload !== 'object' || payload === null) return null
      const radars = (payload as { radars?: unknown }).radars
      if (!Array.isArray(radars) || radars.length === 0) return null

      // Le tri peut tout écarter si la réponse est mal formée : une liste vide
      // mise en cache passerait pour un pays sans radar, et ne serait retentée
      // qu'une semaine plus tard.
      const points = radars.filter(isRadarPoint)
      return points.length > 0 ? points : null
    } catch {
      return null
    }
  }
}

function isRadarPoint(value: unknown): value is RadarPoint {
  if (typeof value !== 'object' || value === null) return false
  const point = value as Record<string, unknown>
  return (
    typeof point.id === 'string' &&
    typeof point.lat === 'number' &&
    typeof point.lon === 'number' &&
    typeof point.speedLimit === 'number'
  )
}

/**
 * Lit une réponse en rapportant les octets reçus.
 *
 * Overpass répond sans longueur annoncée et passe l'essentiel du temps à
 * calculer avant le premier octet : aucun pourcentage n'est calculable, on ne
 * peut afficher qu'une quantité qui progresse.
 */
async function readWithProgress(
  response: Response,
  onBytes: (bytes: number) => void,
): Promise<string> {
  if (!response.body) return response.text()

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (value) {
      chunks.push(value)
      received += value.length
      onBytes(received)
    }
  }

  const merged = new Uint8Array(received)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }

  return new TextDecoder().decode(merged)
}
